import { useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { MediaCan, MinistriesOverview, MinistryDetail, PersonRef } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe, useSession } from '../session/SessionProvider';
import { MinistryMeetingsCard } from '../meetings/MinistryMeetingsCard';
import { ClassroomsCard } from '../classrooms/ClassroomsCard';
import { CellsCard } from './CellsCard';
import { PositionsCard } from './PositionsCard';
import { KIND_TITLES } from './MinistriesPage';
import { CafeteriaSetupCard } from '../worship/CafeteriaCard';
import { MinistryTools } from './MinistryTools';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';

/** Selector de persona + botón, para elegir líder, co-líder o integrante. */
function PickPerson({ id, label, people, action, onPick, busy }: { id: string; label: string; people: PersonRef[]; action: string; onPick: (personId: string) => void; busy: boolean }) {
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const personId = String(new FormData(event.currentTarget).get('personId') ?? '');
    if (personId) onPick(personId);
  }
  if (people.length === 0) return null;
  return (
    <form className="family-add" onSubmit={submit}>
      <div className="field">
        <label htmlFor={id}>{label}</label>
        <SearchSelect id={id} name="personId" options={personOptions(people)} />
      </div>
      <Button type="submit" busy={busy}>
        {action}
      </Button>
    </form>
  );
}

/** Ficha de un Ministerio: líder, co-líderes e integrantes. */
type MediaRights = MediaCan & { operate: boolean };
const MEDIA_RIGHTS: { key: keyof MediaRights; label: string }[] = [
  { key: 'upload', label: 'Subir' },
  { key: 'edit', label: 'Modificar' },
  { key: 'delete', label: 'Eliminar' },
  { key: 'operate', label: 'Operar la consola' },
];

/** Multimedia: lo que el líder le permite a un integrante con los archivos y la consola. */
function MediaRightsToggles({ person, rights, busy, onChange }: { person: PersonRef; rights: MediaRights; busy: boolean; onChange: (rights: MediaRights) => void }) {
  return (
    <span className="media-rights" role="group" aria-label={`Archivos de Multimedia: ${person.name}`}>
      {MEDIA_RIGHTS.map((r) => (
        <label key={r.key} className="media-rights__item">
          <input type="checkbox" checked={rights[r.key]} disabled={busy} onChange={(e) => onChange({ ...rights, [r.key]: e.target.checked })} /> {r.label}
        </label>
      ))}
    </span>
  );
}

export function MinistryPage() {
  const me = useMe();
  const { refresh } = useSession();
  const { ministryId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const churchId = me.activeChurchId;
  const base = `/organization/churches/${churchId}/ministries/${ministryId}`;
  const detail = useAsync((signal) => (churchId ? api.get<MinistryDetail>(base, signal) : Promise.resolve(null)), [base]);
  const overview = useAsync(
    (signal) => (churchId ? api.get<MinistriesOverview>(`/organization/churches/${churchId}/ministries`, signal) : Promise.resolve(null)),
    [churchId],
  );
  const [busy, setBusy] = useState(false);
  // La ficha se lee limpia; los controles (elegir líder, agregar, quitar…) solo aparecen al tocar «Administrar equipo».
  const [editMode, setEditMode] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const isAdmin = overview.status === 'ready' && overview.data?.canAssignLeaders === true;

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setFeedback(null);
    try {
      await action();
      setFeedback({ tone: 'success', text: success });
      detail.reload();
      await refresh(); // un cargo nuevo cambia los permisos de quien lo recibe (si es uno mismo)
    } catch (e) {
      setFeedback({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  if (detail.status === 'loading') return <div className="page"><Loading /></div>;
  if (detail.status === 'error') return <div className="page"><ErrorState error={detail.error} onRetry={detail.reload} /></div>;
  const m = detail.status === 'ready' ? detail.data : null;
  if (!m) return <div className="page"><EmptyState title="Ministerio no encontrado." /></div>;

  const tabs: [string, string][] = [
    ['integrantes', m.kind === 'raices' ? 'Integrantes' : 'Equipo'],
    ...(m.kind === 'raices' ? ([['salones', m.systemKey === 'raices_kids' ? 'Salones por edad' : 'Salones']] as [string, string][]) : []),
    ...(m.cells ? ([['celulas', `Células (${m.cells.length})`]] as [string, string][]) : []),
    ['reuniones', 'Reuniones'],
    ...(m.systemKey === 'cafeteria' ? ([['cultos', 'Cultos']] as [string, string][]) : []),
  ];
  // Raíces abre en sus salones; liderazgo, integrantes y reuniones van en «Administrar equipo» (solo líderes y administración).
  const raices = m.kind === 'raices';
  const canSettings = isAdmin || m.canAssignCoLeader || m.canManageMembers;
  const tab = raices ? (params.get('ver') === 'ajustes' && canSettings ? 'ajustes' : 'salones') : (tabs.find(([k]) => k === params.get('ver'))?.[0] ?? 'integrantes');
  const editing = raices ? tab === 'ajustes' : editMode && (canSettings || isAdmin);
  const inMinistry = new Set([m.leader?.personId, ...m.coLeaders.map((c) => c.personId)]);
  const memberIds = new Set(m.members.map((p) => p.personId));

  return (
    <div className="page">
      <p>
        <Link to="/app/ministerios">← Ministerios</Link>
      </p>
      <header className={`page__header${raices || canSettings || isAdmin ? ' page__header--actions' : ''}`}>
        <div>
          <h1>{m.name}</h1>
          <p className="muted">{raices ? (m.description ?? KIND_TITLES[m.kind].title) : `${KIND_TITLES[m.kind].title}${m.description ? ` · ${m.description}` : ''}`}</p>
        </div>
        {raices && canSettings ? (
          tab === 'ajustes' ? (
            <Button variant="secondary" onClick={() => setParams({}, { replace: true })}>
              Listo
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => setParams({ ver: 'ajustes' }, { replace: true })}>
              Administrar equipo
            </Button>
          )
        ) : canSettings || isAdmin ? (
          <Button variant={editMode ? 'primary' : 'secondary'} onClick={() => setEditMode(!editMode)}>
            {editMode ? 'Listo' : 'Administrar equipo'}
          </Button>
        ) : null}
      </header>
      {feedback ? <Alert tone={feedback.tone}>{feedback.text}</Alert> : null}
      {raices ? null : isAdmin && editing ? (
        <label className="check">
          <input
            type="checkbox"
            checked={Boolean(m.vocation)}
            disabled={busy}
            onChange={(e) => {
              const on = e.target.checked;
              void run(() => api.put(`${base}/vocation`, { on }), on ? 'Ahora es ministerio de vocación: sus líderes pueden publicar vacantes en Mi Vocación.' : 'Ya no es ministerio de vocación.');
            }}
          />{' '}
          Ministerio de vocación <span className="small muted">(sus líderes publican vacantes en Mi Vocación)</span>
        </label>
      ) : m.vocation ? (
        <p className="small">
          <span className="badge badge--active">Ministerio de vocación</span> <Link to="/app/vocacion">Ver vacantes</Link>
        </p>
      ) : null}

      <MinistryTools ministry={m} />

      {raices ? null : (
      <div className="tabs tabs--scroll" role="tablist" aria-label="Secciones del ministerio">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`tabs__tab${tab === key ? ' tabs__tab--active' : ''}`}
            onClick={() => setParams(key === 'integrantes' ? {} : { ver: key }, { replace: true })}
          >
            {label}
          </button>
        ))}
      </div>
      )}

      {tab === 'integrantes' || tab === 'ajustes' ? (
        <>
        <Card title="Liderazgo">
          <dl className="details">
            <dt>Líder</dt>
            <dd>{m.leader ? m.leader.name : <span className="muted">Sin líder</span>}</dd>
            <dt>Co-líder</dt>
            <dd>
              {m.coLeaders.length === 0 ? <span className="muted">Sin co-líder</span> : null}
              {m.coLeaders.map((c) => (
                <span key={c.personId} className="chip-inline">
                  {c.name}
                  {m.canAssignCoLeader && editing ? (
                    <Button variant="ghost" disabled={busy} onClick={() => {
                        if (window.confirm(`¿Quitar a ${c.name} como co-líder de ${m.name}?`)) void run(() => api.delete(`${base}/co-leaders/${c.personId}`), `${c.name} ya no es co-líder.`);
                      }}>
                      Quitar
                    </Button>
                  ) : null}
                </span>
              ))}
            </dd>
          </dl>
          {isAdmin && editing ? (
            <>
              <PickPerson
                id="pick-leader"
                label={m.leader ? 'Cambiar líder' : 'Elegir líder'}
                people={m.candidates.filter((p) => p.personId !== m.leader?.personId)}
                action={m.leader ? 'Cambiar' : 'Asignar'}
                busy={busy}
                onPick={(personId) => void run(() => api.put(`${base}/leader`, { personId }), 'Líder asignado.')}
              />
              {m.leader ? (
                <div className="actions">
                  <Button variant="ghost" disabled={busy} onClick={() => {
                      if (window.confirm(`¿Quitar a ${m.leader!.name} como líder de ${m.name}? El ministerio quedará sin líder.`)) void run(() => api.put(`${base}/leader`, { personId: null }), 'El ministerio quedó sin líder.');
                    }}>
                    Quitar líder
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
          {m.canAssignCoLeader && editing ? (
            <PickPerson
              id="pick-co-leader"
              label="Nombrar co-líder"
              people={m.candidates.filter((p) => !inMinistry.has(p.personId))}
              action="Nombrar"
              busy={busy}
              onPick={(personId) => void run(() => api.post(`${base}/co-leaders`, { personId }), 'Co-líder nombrado.')}
            />
          ) : null}
          {editing ? <p className="small muted">La administración de la iglesia elige al líder; el líder elige a su co-líder.</p> : null}
        </Card>

        <Card title={`Integrantes (${m.members.length})`}>
          {m.automatic ? (
            <Alert tone="info">
              {m.systemKey === 'raices_kids'
                ? 'Raíces Kids incluye automáticamente a todos los miembros del grupo Niños.'
                : 'Raíces incluye automáticamente a todos los miembros, excepto los del grupo Niños (que están en Raíces Kids).'}
            </Alert>
          ) : null}
          {editing && m.systemKey === 'multimedia' ? (
            <p className="small muted">
              Los integrantes de Multimedia ven y descargan lo que hay en Multimedia, como las imágenes que les envía Finanzas para proyectar. Marca a quién le permites también subir, modificar
              (cambiar el nombre) o eliminar (archivar) archivos, y operar la consola de proyección con su cuenta.
            </p>
          ) : null}
          {editing && m.systemKey === 'finanzas' ? (
            <p className="small muted">El líder, los co-líderes y los integrantes que agregues aquí administran Finanzas: cuentas, ingresos y gastos, ofrendas del culto, metas y cierres.</p>
          ) : null}
          {m.kind === 'grupo' && m.canManageMembers && editing ? (
            <p className="small muted">Cada persona pertenece a un solo grupo: si ya estaba en otro, se cambia a este.</p>
          ) : null}
          {m.members.length === 0 ? <p className="muted">Todavía no tiene integrantes.</p> : null}
          <ul className="member-list">
            {m.members.map((p) => (
              <li key={p.personId} className="family-member">
                <Link to={`/app/personas/${p.personId}`} className="family-member__name">
                  {p.name}
                </Link>
                {p.personId === m.leader?.personId ? <span className="badge badge--active">Líder</span> : null}
                {m.coLeaders.some((c) => c.personId === p.personId) ? <span className="badge badge--active">Co-líder</span> : null}
                {(m.positions ?? [])
                  .filter((pos) => pos.members.some((x) => x.personId === p.personId))
                  .map((pos) => (
                    <span key={pos.id} className="badge badge--active">
                      {pos.name}
                    </span>
                  ))}
                {m.mediaRights && editing && !inMinistry.has(p.personId) ? (
                  <MediaRightsToggles
                    person={p}
                    rights={m.mediaRights[p.personId] ?? { upload: false, edit: false, delete: false, operate: false }}
                    busy={busy}
                    onChange={(rights) => void run(() => api.put(`${base}/members/${p.personId}/media-rights`, rights), `Listo: se le avisó a ${p.name}.`)}
                  />
                ) : null}
                {m.canManageMembers && editing ? (
                  <Button variant="ghost" disabled={busy} onClick={() => {
                      if (window.confirm(`¿Quitar a ${p.name} de ${m.name}?`)) void run(() => api.delete(`${base}/members/${p.personId}`), `${p.name} salió de ${m.name}.`);
                    }}>
                    Quitar
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
          {m.canManageMembers && editing ? (
            <PickPerson
              id="pick-member"
              label="Agregar integrante"
              people={m.candidates.filter((p) => !memberIds.has(p.personId))}
              action="Agregar"
              busy={busy}
              onPick={(personId) => void run(() => api.put(`${base}/members/${personId}`, {}), 'Integrante agregado.')}
            />
          ) : null}
        </Card>
        {m.kind === 'servicio' && churchId && m.positions ? <PositionsCard ministry={m} churchId={churchId} run={run} busy={busy} /> : null}
        </>
      ) : null}
      {tab === 'salones' && churchId ? <ClassroomsCard churchId={churchId} ministryId={m.id} kids={m.systemKey === 'raices_kids'} /> : null}
      {tab === 'celulas' && m.cells && churchId ? <CellsCard ministry={m} churchId={churchId} run={run} busy={busy} editing={editing} /> : null}
      {tab === 'cultos' && churchId ? <CafeteriaSetupCard churchId={churchId} /> : null}
      {(tab === 'reuniones' || tab === 'ajustes') && churchId ? <MinistryMeetingsCard churchId={churchId} ministryId={m.id} /> : null}
    </div>
  );
}
